import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/it';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer, toast } from 'react-toastify';
import ThemeContext from '../contexts/themeContext';
import AuthContext from '../contexts/authContext';
import { useOrganization } from '../contexts/organizationContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { UserType } from '../common/data/dummyRolesData';
import authService from '../services/api/authService';
import Loading from '../components/Loading';
import { FeatureErrorModal } from '../components/features';

const App = () => {
	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	// Get navigation and location functions
	const navigate = useNavigate();
	const location = useLocation();

	// Access AuthContext
	const { isAuthenticated, isInitialized, isBootstrapped, login, syncUserData, userData } =
		useContext(AuthContext);

	// Access OrganizationContext
	const { organizations, isLoading: isOrgLoading } = useOrganization();

	// Variable to track if login is in progress to prevent multiple login attempts
	const loginAttemptRef = useRef(false);

	// Handle API timeout errors
	useEffect(() => {
		const handleApiTimeout = (event: CustomEvent) => {
			// Show a user-friendly error message
			toast.error(
				`API request timed out. Please check your internet connection or try again later.`,
				{
					autoClose: 5000,
					closeButton: true,
					position: 'top-right',
				},
			);

			console.error('API timeout:', event.detail);
		};

		// Add event listener
		window.addEventListener('api-timeout-error', handleApiTimeout as EventListener);

		// Clean up
		return () => {
			window.removeEventListener('api-timeout-error', handleApiTimeout as EventListener);
		};
	}, []);

	// Handle role-based redirection after successful authentication
	useEffect(() => {
		if (
			isAuthenticated &&
			userData.role &&
			(location.pathname === '/' || location.pathname === '/login')
		) {
			// If authenticated and on login/home page, redirect to appropriate dashboard based on role
			if (userData.role === UserType.COMPANY) {
				// Check if the user has organizations (only after loading is complete)
				if (!isOrgLoading && organizations && organizations.length > 0) {
					navigate('/company/dashboard');
				} else if (!isOrgLoading && organizations && organizations.length === 0) {
					navigate('/company/organizations');
				}
				// If still loading, don't redirect yet
			} else if (userData.role === UserType.FREELANCE) {
				navigate('/talent/dashboard');
			}
		}
	}, [isAuthenticated, userData.role, location.pathname, navigate, organizations, isOrgLoading]);

	// Handle organization-based redirection for company users
	useEffect(() => {
		if (
			isAuthenticated &&
			userData.role === UserType.COMPANY &&
			!isOrgLoading &&
			organizations &&
			location.pathname === '/company/dashboard'
		) {
			// If company user is on dashboard but has no organizations, redirect to organizations page
			if (organizations.length === 0) {
				navigate('/company/organizations');
			}
		}
	}, [isAuthenticated, userData.role, organizations, isOrgLoading, location.pathname, navigate]);



	// Trigger login if not authenticated and MSAL is initialized
	useEffect(() => {
		// Skip if not initialized or already authenticated
		if (!isInitialized || isAuthenticated) {
			// If authenticated, reset login attempt flag
			if (isAuthenticated) {
				loginAttemptRef.current = false;

				// Sync user data only if essential data is missing to prevent redundant API calls
				if (syncUserData && (!userData.id || !userData.email)) {
					syncUserData().catch((error) => {
						console.error('Failed to sync user data:', error);
						// If sync fails due to invalid token, we should logout and login again
						if (error.response?.status === 401) {
							console.log('Auth token invalid, restarting authentication flow...');
							authService.clearAuth();
							// We'll let the next render cycle handle the login
						}
					});
				}
			}
			return;
		}

		// Only proceed if not authenticated and not already attempting login
		// Don't attempt login on Dashboard pages
		if (!loginAttemptRef.current && !location.pathname.includes('/dashboard')) {
			// Check if access token exists and is valid
			const accessToken = authService.getToken();

			// If no token or token is invalid, trigger login
			if (!accessToken || !authService.isTokenValid(accessToken)) {
				console.log('No valid access token found, initiating Azure AD B2C login flow...');
				// Set flag to prevent multiple login attempts
				loginAttemptRef.current = true;

				// Clear any stale MSAL state first
				Object.keys(localStorage)
					.filter((key) => key.includes('msal.state') || key.includes('msal.interaction'))
					.forEach((key) => localStorage.removeItem(key));

				// Short delay to avoid interaction_in_progress errors
				setTimeout(() => {
					// Trigger the Azure AD B2C login flow - only do this once
					login().catch((error) => {
						console.error('Login error:', error);
						// Don't immediately reset the flag on interaction_in_progress errors
						if (
							error &&
							typeof error === 'object' &&
							'errorCode' in error &&
							error.errorCode === 'interaction_in_progress'
						) {
							// For interaction_in_progress, let the ongoing auth complete
							console.log('Waiting for ongoing authentication to complete...');
						} else {
							// Reset flag for other errors so we can try again
							loginAttemptRef.current = false;
						}
					});
				}, 500);
			}
		}
	}, [
		isInitialized,
		isAuthenticated,
		login,
		syncUserData,
		userData.id,
		userData.email,
		location.pathname,
	]);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					{isInitialized && isAuthenticated && isBootstrapped ? (
						<>
							<AsideRoutes />
							<Wrapper />
						</>
					) : (
						<Loading />
					)}
				</div>
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
				<FeatureErrorModal />
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;
